// Share Performance Card — renders a 1080x1350 (Instagram portrait) branded PNG
// of the user's equity curve. Pure HTML5 Canvas, no dependencies.
//
// Usage:
//   const blob = await renderShareCard({
//     traderName, period, periodLabel,
//     currentEquity, startingBalance, periodReturn, periodPnl,
//     chartData,    // [{value: percent, raw: equity}]
//     isPositive,
//   });
//   downloadBlob(blob, 'profitpulse-performance.png');
//
// Visual design:
//   - Black background with subtle radial gradient
//   - "PROFIT PULSE" wordmark top-left, period pill top-right
//   - Hero balance + ROI in the upper third
//   - Chart fills the middle ~45% of the card
//   - Trader name + URL footer

const W = 1080;
const H = 1350;

// Public helper — turn a Blob into a download
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// Try the native Web Share API; falls back to download. Returns true if shared.
export async function shareBlob(blob, filename, title, text) {
  try {
    const file = new File([blob], filename, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title, text });
      return true;
    }
  } catch (_e) { /* user cancelled or denied — fall through */ }
  downloadBlob(blob, filename);
  return false;
}

export async function renderShareCard(opts) {
  const {
    traderName    = 'Trader',
    period        = '1Y',
    periodLabel   = 'Past year',
    currentEquity = 0,
    startingBalance = 0,
    periodReturn  = 0,        // in percent (e.g. 12 for +12%)
    periodPnl     = 0,        // in dollars
    chartData     = [],       // [{value, raw, t?}]
    isPositive    = true,
  } = opts;

  const dpr = 2; // export at 2x for retina
  const canvas = document.createElement('canvas');
  canvas.width  = W * dpr;
  canvas.height = H * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  // ── Background ─────────────────────────────────────────────────────────
  ctx.fillStyle = '#050505';
  ctx.fillRect(0, 0, W, H);
  // Subtle radial glow behind the chart matching the trend color
  const glowColor = isPositive ? 'rgba(0,255,148,0.18)' : 'rgba(255,51,51,0.18)';
  const grad = ctx.createRadialGradient(W/2, H*0.55, 60, W/2, H*0.55, W*0.7);
  grad.addColorStop(0, glowColor);
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // ── Header — wordmark + period pill ────────────────────────────────────
  ctx.font = '700 36px system-ui, -apple-system, "Helvetica Neue", Arial';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#2979FF';
  ctx.fillText('PROFIT', 70, 70);
  const profitWidth = ctx.measureText('PROFIT').width;
  ctx.fillStyle = '#9B2335';
  ctx.fillText('PULSE', 70 + profitWidth + 6, 70);

  // Period pill (top-right)
  const pillW = 130, pillH = 44, pillX = W - 70 - pillW, pillY = 70;
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 1;
  roundedRect(ctx, pillX, pillY, pillW, pillH, 22);
  ctx.fill(); ctx.stroke();
  ctx.font = '700 16px system-ui, -apple-system, "Helvetica Neue", Arial';
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(periodLabel.toUpperCase(), pillX + pillW/2, pillY + pillH/2 + 1);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // ── Hero block — TOTAL PORTFOLIO + balance + ROI ───────────────────────
  let y = 200;
  ctx.font = '600 18px system-ui, -apple-system, "Helvetica Neue", Arial';
  ctx.fillStyle = 'rgba(161,161,170,0.7)';
  ctx.fillText('TOTAL PORTFOLIO', 70, y);
  y += 30;

  ctx.font = '800 96px "SF Mono", "JetBrains Mono", Menlo, monospace';
  ctx.fillStyle = '#FFFFFF';
  const balanceTxt = `$${Math.round(currentEquity).toLocaleString()}`;
  ctx.fillText(balanceTxt, 70, y);
  y += 110;

  // ROI row — green/red %
  ctx.font = '800 56px "SF Mono", "JetBrains Mono", Menlo, monospace';
  const roiColor = isPositive ? '#00FF94' : '#FF3333';
  ctx.fillStyle = roiColor;
  const sign = periodReturn >= 0 ? '+' : '';
  // Format ROI: 2dp for normal %, comma-grouped int for >1000% (preserves layout)
  const roiFormatted = (Math.abs(periodReturn) >= 1000)
    ? Math.round(periodReturn).toLocaleString()
    : Number(periodReturn).toFixed(2);
  const roiTxt = `${sign}${roiFormatted}%`;
  ctx.fillText(roiTxt, 70, y);

  // PnL inline next to ROI
  const roiW = ctx.measureText(roiTxt).width;
  ctx.font = '700 36px "SF Mono", "JetBrains Mono", Menlo, monospace';
  ctx.fillStyle = 'rgba(228,228,231,0.85)';
  ctx.fillText(`  ${sign}$${Math.round(Math.abs(periodPnl)).toLocaleString()}`, 70 + roiW, y + 18);
  y += 90;

  // Started value (small)
  ctx.font = '500 22px system-ui, -apple-system, "Helvetica Neue", Arial';
  ctx.fillStyle = 'rgba(161,161,170,0.7)';
  ctx.fillText(`Started at  `, 70, y);
  const startedW = ctx.measureText('Started at  ').width;
  ctx.fillStyle = '#2979FF';
  ctx.font = '700 22px "SF Mono", "JetBrains Mono", Menlo, monospace';
  ctx.fillText(`$${Math.round(startingBalance).toLocaleString()}`, 70 + startedW, y);
  y += 55;

  // ── Chart ──────────────────────────────────────────────────────────────
  drawChart(ctx, chartData || [], 70, y, W - 140, 460, roiColor);
  y += 460;

  // ── Footer ─────────────────────────────────────────────────────────────
  y = H - 200;

  // Divider line
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(70, y, W - 140, 1);
  y += 36;

  // Trader name
  ctx.font = '800 38px system-ui, -apple-system, "Helvetica Neue", Arial';
  ctx.fillStyle = '#FFFFFF';
  ctx.fillText(traderName, 70, y);
  y += 50;

  // Tagline + URL
  ctx.font = '500 20px system-ui, -apple-system, "Helvetica Neue", Arial';
  ctx.fillStyle = 'rgba(161,161,170,0.85)';
  ctx.fillText('Copy-trading institutional strategies', 70, y);
  y += 30;

  ctx.font = '700 20px "SF Mono", "JetBrains Mono", Menlo, monospace';
  ctx.fillStyle = '#2979FF';
  ctx.fillText('profit-pulse.pro', 70, y);

  return new Promise(resolve => canvas.toBlob(b => resolve(b), 'image/png', 0.95));
}

// ── helpers ──────────────────────────────────────────────────────────────
function roundedRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

function drawChart(ctx, data, x, y, w, h, color) {
  if (!data || data.length < 2) {
    ctx.font = '500 22px system-ui, -apple-system, "Helvetica Neue", Arial';
    ctx.fillStyle = 'rgba(161,161,170,0.4)';
    ctx.textAlign = 'center';
    ctx.fillText('No equity data yet', x + w/2, y + h/2);
    ctx.textAlign = 'left';
    return;
  }
  const padTop = 20, padBot = 30, padLeft = 80, padRight = 20;
  const innerW = w - padLeft - padRight;
  const innerH = h - padTop - padBot;
  const values = data.map(d => d.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const range = maxV - minV || 1;
  const getX = (i) => x + padLeft + (i / (data.length - 1)) * innerW;
  const getY = (v) => y + padTop + innerH - ((v - minV) / range) * innerH;

  // Y-axis grid + labels (5 lines)
  ctx.font = '600 16px "SF Mono", "JetBrains Mono", Menlo, monospace';
  ctx.fillStyle = 'rgba(161,161,170,0.55)';
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 1;
  for (let i = 0; i <= 4; i++) {
    const v = minV + (i / 4) * range;
    const yy = getY(v);
    ctx.beginPath();
    ctx.moveTo(x + padLeft, yy);
    ctx.lineTo(x + w - padRight, yy);
    ctx.stroke();
    const sign = v >= 0 ? '+' : '';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${sign}${v.toFixed(0)}%`, x + padLeft - 8, yy);
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Gradient fill under line
  const fillGrad = ctx.createLinearGradient(0, y + padTop, 0, y + h - padBot);
  fillGrad.addColorStop(0, color + '70');
  fillGrad.addColorStop(1, color + '00');
  ctx.fillStyle = fillGrad;
  ctx.beginPath();
  ctx.moveTo(getX(0), y + h - padBot);
  data.forEach((d, i) => ctx.lineTo(getX(i), getY(d.value)));
  ctx.lineTo(getX(data.length - 1), y + h - padBot);
  ctx.closePath();
  ctx.fill();

  // Line
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  data.forEach((d, i) => {
    const xx = getX(i), yy = getY(d.value);
    if (i === 0) ctx.moveTo(xx, yy); else ctx.lineTo(xx, yy);
  });
  ctx.stroke();

  // Final dot
  const lastX = getX(data.length - 1);
  const lastY = getY(data[data.length - 1].value);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(lastX, lastY, 8, 0, Math.PI * 2);
  ctx.fill();
  // Halo
  ctx.fillStyle = color + '33';
  ctx.beginPath();
  ctx.arc(lastX, lastY, 18, 0, Math.PI * 2);
  ctx.fill();
}
